import { Controller } from '@hotwired/stimulus';

/**
 * planning-dependency-picker — drives the "Dépendances (phases prérequises)"
 * picker in the Planning phase form: a chips row of selected phases plus a
 * directly-visible, searchable checklist of phase checkboxes. The picker lives
 * under the phase-form « Dépendances » disclosure section, whose chevron handles
 * open/close — the picker has NO internal show/hide toggle of its own.
 *
 *   • filter       — narrow the checklist rows by code/label substring
 *   • updateChips  — rebuild the chips row from the checked checkboxes, sync the
 *                    delay rows, then emit `planning-dependency-picker:changed`
 *   • removeChip   — uncheck the matching box from a chip's ✕ then re-sync
 *
 * Chips are KEYED by dataset.id (the submitted phase id — RV-1), but still
 * DISPLAY the code + label (data-code / data-label) so the user sees the same
 * chip the server rendered. The filter searches by code/label substring. The ✕ of
 * a JS-built chip carries `relative` + the `Ui::ButtonComponent::TEXT_TAP_TARGET`
 * tokens (`TAP_TARGET_CLASSES` below, a COPY: keep both in step), like the ✕ the
 * server renders (44 px target, WCAG 2.5.8).
 *
 * ── TABLEAU DES DÉLAIS (délai entre lots, T6 ; Q-DL-25, R-06) ───────────────
 * When the host names the delay table's `<tbody>` (`delayBodyId`, inside the
 * `<form>`) and the row `<template data-dependency-delay-row-template>`
 * (`delayTemplateId`, a SIBLING of the `<form>`: its fields must never post),
 * every sync keeps ONE row per checked box in that body:
 *   • checked, row present (server-rendered or cloned earlier) → shown again
 *     (`hidden` off) and its `[data-delay-field]` fields re-enabled: the values
 *     typed before an uncheck come back;
 *   • checked, no row → CLONED from the template (never moved): the tokens
 *     `__ID__` / `__LABEL__` are replaced in the attributes of `TOKEN_ATTRIBUTES`
 *     only, the code / label cells (`[data-delay-row-code]`, `[data-delay-row-label]`)
 *     are filled by `textContent`, and `data-predecessor-label` / `data-pred-start` /
 *     `data-pred-end` are copied from the box (`data-label`, `data-start-date`,
 *     `data-end-date`) for the « au plus tôt » notice of `planning-phase-form`;
 *   • unchecked → the row is HIDDEN and its fields `disabled` (a disabled field is
 *     not submitted), never removed.
 * A box just CHECKED (its `change`, not a chip's ✕, not connect) scrolls the drawer to
 * its row (`scrollIntoView({ block: 'nearest' })`, `smooth` unless reduced motion), the
 * focus left on the box (retour Nicolas 16/10).
 * No colour, no markup and no text is written here: everything comes from the
 * server template. A user label never goes through `innerHTML` (XSS #591).
 * Without the two ids (Lookbook preview, another caller) only the chips move.
 *
 * Targets: chips, search, option, checkbox, empty, removeIcon
 * Values:
 *   removeLabel     (String, default "Retirer la dépendance")
 *   delayBodyId     (String, '' = no delay table) — id of the rows' `<tbody>`
 *   delayTemplateId (String, '' = no delay table) — id of the row `<template>`
 * Actions:
 *   filter      (input on the search box)
 *   updateChips (change on each checkbox; reads `event.target` to scroll to a checked row)
 *   removeChip  (from a rebuilt chip's ✕ — reads dataset.id)
 * Events:
 *   planning-dependency-picker:changed (bubbles) — once per user toggle (never on
 *   connect); `planning-phase-form` listens to it on its root to refresh the
 *   « au plus tôt » notice.
 *
 * @example
 * <div data-controller="planning-dependency-picker"
 *      data-planning-dependency-picker-remove-label-value="Retirer la dépendance"
 *      data-planning-dependency-picker-delay-body-id-value="phase-dependency-delays-body"
 *      data-planning-dependency-picker-delay-template-id-value="phase-dependency-delays-template">
 *   <div data-planning-dependency-picker-target="chips"></div>
 *   <div>
 *     <input type="text" data-planning-dependency-picker-target="search"
 *            data-action="input->planning-dependency-picker#filter">
 *     <ul>
 *       <li data-planning-dependency-picker-target="option" data-id="42" data-code="P1" data-label="Gros œuvre">
 *         <input type="checkbox" value="42" data-planning-dependency-picker-target="checkbox"
 *                data-id="42" data-code="P1" data-label="Gros œuvre"
 *                data-start-date="2026-10-05" data-end-date="2026-10-09"
 *                data-action="change->planning-dependency-picker#updateChips">
 *       </li>
 *       <li data-planning-dependency-picker-target="empty" class="hidden">Aucun résultat</li>
 *     </ul>
 *   </div>
 * </div>
 */

// `Ui::ButtonComponent::TEXT_TAP_TARGET` (app/components/ui/button_component.rb) + `relative`:
// an invisible ≥ 44 px pseudo-element over the small ✕ of a chip. Keep in step with the constant.
const TAP_TARGET_CLASSES = [
  'relative',
  "after:content-['']",
  'after:absolute',
  'after:left-1/2',
  'after:top-1/2',
  'after:-translate-x-1/2',
  'after:-translate-y-1/2',
  'after:w-[max(100%,44px)]',
  'after:h-[max(100%,44px)]',
].join(' ');

// The only attributes of a cloned delay row whose tokens are replaced (ids, names, the
// a11y wiring and the row key). Values are set with `setAttribute`: never parsed as HTML.
const TOKEN_ATTRIBUTES = Object.freeze([
  'id',
  'name',
  'for',
  'aria-label',
  'aria-describedby',
  'aria-labelledby',
  'aria-errormessage',
  'data-predecessor-id',
]);
const ID_TOKEN = '__ID__';
const LABEL_TOKEN = '__LABEL__';

export default class extends Controller {
  static targets = ['chips', 'search', 'option', 'checkbox', 'empty', 'removeIcon'];

  static values = {
    removeLabel: { type: String, default: 'Retirer la dépendance' },
    delayBodyId: String,
    delayTemplateId: String,
  };

  connect() {
    // Reflect the initial checked state in the chips row and the delay rows. No
    // `changed` event: nothing was toggled (the form reads its own first paint).
    this.#sync();
  }

  filter() {
    if (!this.hasSearchTarget) return;

    const query = this.#normalize(this.searchTarget.value.trim());
    let visibleCount = 0;

    this.optionTargets.forEach((option) => {
      const code = this.#normalize(option.dataset.code || '');
      const label = this.#normalize(option.dataset.label || '');
      const matches = query === '' || code.includes(query) || label.includes(query);

      option.classList.toggle('hidden', !matches);
      if (matches) visibleCount += 1;
    });

    if (this.hasEmptyTarget) {
      const hasOptions = this.optionTargets.length > 0;
      this.emptyTarget.classList.toggle('hidden', !(hasOptions && visibleCount === 0));
    }
  }

  // Accent-insensitive folding for French labels (é→e, ç→c, œ stays œ but the
  // query is folded the same way). Mirrors contract_selector_controller#normalize.
  #normalize(text) {
    return String(text)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  }

  // Fired on `change` of each checkbox (and by `removeChip`): chips + delay rows, then
  // ONE bubbling `planning-dependency-picker:changed` for the form's « au plus tôt », then
  // the drawer scrolls to the row of a box just CHECKED (see `#revealDelayRow`).
  updateChips(event) {
    this.#sync();
    this.dispatch('changed');
    this.#revealDelayRow(event?.target);
  }

  #sync() {
    const checkboxes = this.hasCheckboxTarget ? this.checkboxTargets : [];
    this.#renderChips(checkboxes.filter((checkbox) => checkbox.checked));
    this.#syncDelayRows(checkboxes);
  }

  #renderChips(checked) {
    if (!this.hasChipsTarget) return;

    this.chipsTarget.innerHTML = checked.map((checkbox) => this.#chipMarkup(checkbox)).join('');
  }

  // ── Tableau des délais (see the header) ──────────────────────────────────────

  #syncDelayRows(checkboxes) {
    const body = this.#elementById(this.delayBodyIdValue);
    if (!body) return;

    const template = this.#elementById(this.delayTemplateIdValue);
    checkboxes.forEach((checkbox) => {
      const id = checkbox.dataset.id || '';
      if (id === '') return;

      const row = this.#delayRowFor(body, id);
      if (row) {
        this.#showDelayRow(row, checkbox.checked);
      } else if (checkbox.checked && template) {
        const clone = this.#cloneDelayRow(template, checkbox);
        if (clone) body.appendChild(clone);
      }
    });
  }

  // Retour Nicolas 16/10 (chantier 59): the row of a prerequisite just checked lands UNDER
  // the list, out of view at the bottom of the drawer. Every scrolling ancestor (the
  // drawer body) scrolls just enough to show it (`block: 'nearest'`); the focus stays on
  // the box (the user keeps checking). Only a `change` that CHECKED a box: never on an
  // uncheck, a chip's ✕ (no event) or connect. Runs AFTER `changed`, so the « au plus
  // tôt » notice above already has its final height. Reduced motion → no smooth scroll.
  #revealDelayRow(checkbox) {
    if (!checkbox?.checked) return;

    const body = this.#elementById(this.delayBodyIdValue);
    const id = checkbox.dataset?.id || '';
    const row = body && id ? this.#delayRowFor(body, id) : null;
    if (!row) return;

    const reduce = globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    row.scrollIntoView?.({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
  }

  #elementById(id) {
    return id ? document.getElementById(id) : null;
  }

  // Compared on the dataset (no CSS escaping of an id inside a selector).
  #delayRowFor(body, id) {
    return Array.from(body.querySelectorAll('[data-predecessor-id]')).find(
      (row) => row.dataset.predecessorId === id
    );
  }

  // Q-DL-25: an unchecked prerequisite's row is hidden and its fields disabled (not
  // submitted); checking it again gives back the values typed before.
  #showDelayRow(row, shown) {
    row.hidden = !shown;
    row.querySelectorAll('[data-delay-field]').forEach((field) => {
      field.disabled = !shown;
    });
  }

  // A CLONE of the server template's row (the template keeps it), named for this
  // prerequisite: tokens replaced in attribute values, code and label by `textContent`.
  #cloneDelayRow(template, checkbox) {
    const source = template.content?.firstElementChild;
    if (!source) return null;

    const row = source.cloneNode(true);
    const id = checkbox.dataset.id || '';
    const label = checkbox.dataset.label || '';
    [row, ...row.querySelectorAll('*')].forEach((element) => {
      TOKEN_ATTRIBUTES.forEach((attribute) => {
        const value = element.getAttribute(attribute);
        if (value === null) return;

        element.setAttribute(
          attribute,
          value.split(ID_TOKEN).join(id).split(LABEL_TOKEN).join(label)
        );
      });
    });

    const code = row.querySelector('[data-delay-row-code]');
    if (code) code.textContent = checkbox.dataset.code || '';
    const labelCell = row.querySelector('[data-delay-row-label]');
    if (labelCell) labelCell.textContent = label;

    row.dataset.predecessorLabel = label;
    row.dataset.predStart = checkbox.dataset.startDate || '';
    row.dataset.predEnd = checkbox.dataset.endDate || '';
    this.#showDelayRow(row, true);
    return row;
  }

  removeChip(event) {
    const id = event.currentTarget.dataset.id;
    if (!id || !this.hasCheckboxTarget) return;

    const checkbox = this.checkboxTargets.find((box) => box.dataset.id === id);
    if (!checkbox) return;

    checkbox.checked = false;
    this.updateChips();
  }

  #chipMarkup(checkbox) {
    // `id` keys the chip (matches the checkbox on removeChip — RV-1); `code` and
    // `label` are display-only, mirroring the server-rendered chip exactly.
    const id = this.#escapeHtml(checkbox.dataset.id || '');
    const code = this.#escapeHtml(checkbox.dataset.code || '');
    const label = this.#escapeHtml(checkbox.dataset.label || '');
    const removeLabel = this.#escapeHtml(this.removeLabelValue);
    // Clone the server-rendered Ui::IconComponent (x_mark) so the JS-built chip
    // matches the server chip exactly and the icon stays authored in ERB (never
    // hand-written <svg> in JS — keeps it lint-visible / single-sourced).
    const icon = this.hasRemoveIconTarget ? this.removeIconTarget.innerHTML : '';

    return [
      '<span class="inline-flex items-center gap-1.5 rounded-md bg-neutral-100 py-1 pl-2 pr-1 text-xs">',
      `<span class="font-semibold text-neutral-700">${code}</span>`,
      `<span class="max-w-[140px] truncate text-neutral-600">${label}</span>`,
      '<button type="button" ',
      'class="cursor-pointer rounded p-0.5 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 ',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ',
      `${TAP_TARGET_CLASSES}" `,
      'data-action="planning-dependency-picker#removeChip" ',
      `data-id="${id}" aria-label="${removeLabel}">`,
      icon,
      '</button>',
      '</span>',
    ].join('');
  }

  #escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}
